import { FC, PropsWithChildren, useContext, useEffect, useRef } from 'react';

import {
  CollapseButton,
  HorizontalDragHandler,
  LeftPanel,
  LeftPanelSharedProps,
  PanelContent,
  PanelTitle,
  PanelTitleActions,
} from 'dashboard/components-lib/NavAndPanelsOverlay/LeftPanelShared';
import { NavAndPanelsOverlayContext } from 'dashboard/components-lib/NavAndPanelsOverlay/NavAndPanelsOverlay';

const DEFAULT_PANEL_WIDTH_IN_PX = 300;
const MAX_PANEL_WIDTH_IN_PX = 600;

const SmallLeftPanel: FC<PropsWithChildren<LeftPanelSharedProps>> = ({
  title,
  basePath,
  children,
}) => {
  const { panelsOverlayRef } = useContext(NavAndPanelsOverlayContext);
  const panelRef = useRef<HTMLDivElement>(null);

  // Initialize panel width
  useEffect(() => {
    if (panelRef.current) {
      panelRef.current.style.width = `${DEFAULT_PANEL_WIDTH_IN_PX}px`;
    }
  }, [panelRef]);

  return (
    <LeftPanel ref={panelRef}>
      <HorizontalDragHandler
        panelRef={panelRef}
        onMoveTo={(cursorX) => {
          // The panel width is dynamically calculated to be a percentage of the overlay. This allows
          // the panel to scale relatively with the window when the window is resized. We additionally
          // bound the panel width by pixel values to enforce an absolute minimum and maximum width.
          if (panelRef.current) {
            const overlayWidthInPx = panelsOverlayRef?.current?.clientWidth ?? 0;
            const panelX = panelRef.current.getBoundingClientRect().x;
            const newPanelWidthInPx = cursorX - panelX;
            const newPanelWidthInPercent = (newPanelWidthInPx / overlayWidthInPx) * 100;
            panelRef.current.style.width = `min(max(${newPanelWidthInPercent}%, ${DEFAULT_PANEL_WIDTH_IN_PX}px), ${MAX_PANEL_WIDTH_IN_PX}px)`;
          }
        }}
      />

      <PanelTitle>
        {title}
        <PanelTitleActions>
          <CollapseButton basePath={basePath} />
        </PanelTitleActions>
      </PanelTitle>
      <PanelContent>{children}</PanelContent>
    </LeftPanel>
  );
};

export default SmallLeftPanel;
